const TAP_MOVEMENT_PX = 12;
const CHAPTER_SWIPE_PX = 60;
const VERTICAL_DOMINANCE_RATIO = 1.25;
const CONTROLS_TIMEOUT_MS = 3000;
const CHAPTER_NAV_COOLDOWN_MS = 600;
const INTERACTIVE_SELECTOR = [
	'button',
	'a',
	'input',
	'select',
	'textarea',
	'[role="button"]',
	'.vf-progress-shell',
	'.vf-chapter-dots',
	'.vf-dot',
].join(',');

export interface VideoGestureCommands {
	onNext(): void;
	onPrevious(): void;
}

type GestureIntent = 'pending' | 'vertical' | 'cancelled';

interface ActiveGesture {
	pointerId: number;
	startX: number;
	startY: number;
	lastY: number;
	directionChanges: number;
	intent: GestureIntent;
}

interface GesturePoint {
	x: number;
	y: number;
}

export function wireVideoGestureController(surface: HTMLElement, commands: VideoGestureCommands): () => void {
	const abort = typeof AbortController === 'undefined' ? null : new AbortController();
	const options = abort ? { signal: abort.signal } : undefined;
	let active: ActiveGesture | null = null;
	let hideTimer: number | undefined;
	let disposed = false;
	let lastChapterNavAt = 0;

	const hide = () => {
		surface.classList.remove('controls-visible');
		surface.classList.add('controls-idle');
	};
	const reveal = () => {
		surface.classList.add('controls-visible');
		surface.classList.remove('controls-idle');
		if (hideTimer !== undefined) window.clearTimeout(hideTimer);
		hideTimer = window.setTimeout(hide, CONTROLS_TIMEOUT_MS);
	};
	const toggle = () => {
		if (surface.classList.contains('controls-visible')) hide();
		else reveal();
	};
	const reset = (releaseCapture = true) => {
		if (releaseCapture && active && active.pointerId >= 0) {
			try { surface.releasePointerCapture?.(active.pointerId); } catch { /* Pointer capture may already be gone. */ }
		}
		active = null;
	};
	const isExcluded = (target: EventTarget | null) => target instanceof Element && target.closest(INTERACTIVE_SELECTOR) !== null;
	const begin = (pointerId: number, point: GesturePoint, target: EventTarget | null) => {
		if (active || isExcluded(target)) return false;
		active = {
			pointerId,
			startX: point.x,
			startY: point.y,
			lastY: point.y,
			directionChanges: 0,
			intent: 'pending',
		};
		return true;
	};
	const update = (point: GesturePoint, preventDefault?: () => void) => {
		if (!active) return;
		const dx = point.x - active.startX;
		const dy = point.y - active.startY;
		const stepY = point.y - active.lastY;
		const previousStepY = active.lastY - active.startY;
		if (active.intent === 'pending' && Math.sign(stepY) !== 0 && Math.sign(previousStepY) !== 0 && Math.sign(stepY) !== Math.sign(previousStepY)) {
			active.directionChanges += 1;
		}
		active.lastY = point.y;
		if (active.intent !== 'pending' || Math.hypot(dx, dy) < TAP_MOVEMENT_PX) return;
		if (active.directionChanges > 1) {
			active.intent = 'cancelled';
			return;
		}
		active.intent = Math.abs(dy) >= VERTICAL_DOMINANCE_RATIO * Math.abs(dx) ? 'vertical' : 'cancelled';
		if (active.intent === 'vertical') {
			preventDefault?.();
			hide();
		}
	};
	const complete = (point: GesturePoint) => {
		if (!active) return;
		const gesture = active;
		reset();
		const dx = point.x - gesture.startX;
		const dy = point.y - gesture.startY;
		if (gesture.intent === 'vertical') {
			hide();
			if (Math.abs(dy) >= CHAPTER_SWIPE_PX) {
				// Cooldown to prevent double-firing from iOS Safari's
				// event quirks (pointercancel + pointerup sequences,
				// or the video element previously swallowing events
				// and releasing them as a burst). One swipe = one chapter.
				const now = Date.now();
				if (now - lastChapterNavAt < CHAPTER_NAV_COOLDOWN_MS) return;
				lastChapterNavAt = now;
				if (dy < 0) commands.onNext();
				else commands.onPrevious();
			}
			return;
		}
		if (gesture.intent === 'pending' && Math.hypot(dx, dy) < TAP_MOVEMENT_PX) toggle();
	};

	const onPointerDown = (event: PointerEvent) => {
		// Tapping the video area must toggle the controls on every pointer
		// type (touch, mouse, pen). The previous `pointerType === 'touch'`
		// filter left desktop chromium / Chrome mobile-emulation pointer
		// events stuck — pointerType there is "mouse", so the early-return
		// silently swallowed every tap and the controls never revealed.
		// `begin()` already ignores taps that land on interactive children
		// (buttons, progress shell, dots), so removing the filter is safe.
		if (!event.isPrimary) {
			reset();
			return;
		}
		if (!begin(event.pointerId, { x: event.clientX, y: event.clientY }, event.target)) return;
		try { surface.setPointerCapture?.(event.pointerId); } catch { /* Safari can reject capture during native video handoff. */ }
	};
	const onPointerMove = (event: PointerEvent) => {
		if (!active || event.pointerId !== active.pointerId) return;
		update({ x: event.clientX, y: event.clientY }, () => event.preventDefault());
	};
	const onPointerUp = (event: PointerEvent) => {
		if (!active || event.pointerId !== active.pointerId) return;
		complete({ x: event.clientX, y: event.clientY });
	};
	const onPointerCancel = (event: PointerEvent) => {
		if (active?.pointerId === event.pointerId) reset();
	};
	const onMouseMove = () => {
		if (typeof window.matchMedia === 'function' && !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
		reveal();
	};

	hide();
	surface.addEventListener('pointerdown', onPointerDown, options);
	surface.addEventListener('pointermove', onPointerMove, options);
	surface.addEventListener('pointerup', onPointerUp, options);
	surface.addEventListener('pointercancel', onPointerCancel, options);
	surface.addEventListener('mousemove', onMouseMove, options);
	surface.addEventListener('focusin', reveal, options);
	surface.addEventListener('keydown', reveal, options);

	return () => {
		if (disposed) return;
		disposed = true;
		reset();
		if (hideTimer !== undefined) window.clearTimeout(hideTimer);
		if (abort) abort.abort();
		else {
			surface.removeEventListener('pointerdown', onPointerDown);
			surface.removeEventListener('pointermove', onPointerMove);
			surface.removeEventListener('pointerup', onPointerUp);
			surface.removeEventListener('pointercancel', onPointerCancel);
			surface.removeEventListener('mousemove', onMouseMove);
			surface.removeEventListener('focusin', reveal);
			surface.removeEventListener('keydown', reveal);
		}
	};
}
